import type { DataTableSort } from '@bcecloud/portal-ui';

/**
 * Client-side filter + sort for the directory facet tables (cities, venues,
 * accounts) — these lists arrive fully loaded (≤500 rows), so the browser is
 * the right place to slice them.
 */

/** Case-insensitive substring match across the given fields. */
export function filterRows<T>(
  rows: readonly T[],
  search: string,
  fields: ReadonlyArray<keyof T>,
): T[] {
  const needle = search.trim().toLowerCase();
  if (needle === '') return [...rows];
  return rows.filter((row) =>
    fields.some((field) => {
      const value = row[field];
      return typeof value === 'string' && value.toLowerCase().includes(needle);
    }),
  );
}

/** Sorts by one key: numbers numerically, strings alphabetically, null last. */
export function sortRows<T>(rows: readonly T[], sort: DataTableSort | undefined): T[] {
  if (sort === undefined) return [...rows];
  const factor = sort.direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = (a as Record<string, unknown>)[sort.key];
    const right = (b as Record<string, unknown>)[sort.key];
    if (left === null || left === undefined) return right === null || right === undefined ? 0 : 1;
    if (right === null || right === undefined) return -1;
    if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
    return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * factor;
  });
}
